import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { PhoneCallView, usePhoneCallFilterQuery } from '../../../../queries/PhoneCall/usePhoneCallFilterQuery';
import { Button } from '../../../../web/components-v2/Button';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { SimpleTooltip } from '../../../../web/components/Tooltip';
import { DuplicateIcon } from '../../../../web/components/svgs/icons/DuplicateIcon';
import { InfoIcon } from '../../../../web/components/svgs/icons/InfoIcon';
import { IPhoneCallFilterCriteria, PhoneCallFilterCriteriaProperty } from '../../../viewModels/phonecall';
import { CompanyLink } from './CompanyLink';
import { InfiniteListStates } from './InfiniteListStates';
import { RecordingCell } from './RecordingCell';
import { styleSheet } from './styles';

const INCOMING_COLUMN_LABELS = ['Number', 'Company', 'Time', 'Recording'];

interface IRowProps {
	call: PhoneCallView;
	isSelected: boolean;
	onOpenCall: (callId: string, options?: { autoplay?: boolean }) => void;
}

const IncomingCallRow: React.FC<IRowProps> = ({ call, isSelected, onOpenCall }) => {
	const { phoneCall, company } = call;
	const number = phoneCall?.phoneNumber?.standard || 'Unknown';
	const dialed = phoneCall?.creatorPhoneNumber?.standard;
	const onCopyNumber = () => {
		if (copyToClipboard(number)) {
			toast.success('Number copied to clipboard');
		} else {
			toast.error('Unable to copy number');
		}
	};

	return (
		<div
			className={css(styleSheet.row, styleSheet.incomingGrid, isSelected && styleSheet.rowSelected)}
			aria-current={isSelected || undefined}
		>
			{phoneCall?.id ? (
				<Button
					type='button'
					variant='ghost'
					aria-label={`View call details for ${number}`}
					styles={[styleSheet.openRow]}
					onClick={() => onOpenCall(phoneCall.id)}
				/>
			) : null}
			<div className={css(styleSheet.number)}>
				<span>{number}</span>
				<Button
					type='button'
					variant='ghost'
					size='icon'
					aria-label='Copy number'
					styles={[styleSheet.raised]}
					onClick={onCopyNumber}
				>
					<DuplicateIcon fillColor='currentColor' />
				</Button>
				{dialed ? (
					<SimpleTooltip title={`Dialed: ${dialed}`}>
						<span
							className={css(styleSheet.recordingIcon, styleSheet.raised)}
							aria-label={`Dialed: ${dialed}`}
							role='img'
						>
							<InfoIcon fillColor={THEME.colors.icon.gray} />
						</span>
					</SimpleTooltip>
				) : null}
			</div>
			<div>
				<CompanyLink companyId={company?.id} companyName={company?.companyName} />
			</div>
			<span className={css(styleSheet.mutedSmall)}>
				{moment(phoneCall?.lastModifiedDate).format('ddd, MM/DD/YY, h:mmA')}
			</span>
			<RecordingCell
				phoneCall={phoneCall}
				showTranscription={false}
				onPlay={() => phoneCall?.id && onOpenCall(phoneCall.id, { autoplay: true })}
			/>
		</div>
	);
};

interface IProps {
	selectedCallId?: string;
	onOpenCall: (callId: string, options?: { autoplay?: boolean }) => void;
}

export const IncomingCallList: React.FC<IProps> = ({ selectedCallId, onOpenCall }) => {
	const userSession = useUserSession();
	const userId = userSession.user?.id;
	const criteria = React.useMemo<IPhoneCallFilterCriteria[]>(
		() => [
			{ property: PhoneCallFilterCriteriaProperty.User, value: userId },
			{ property: PhoneCallFilterCriteriaProperty.Direction, value: 'Inbound' },
		],
		[userId]
	);
	const query = usePhoneCallFilterQuery({ criteria, enabled: !!userId });
	const calls = query.data?.calls ?? [];

	return (
		<InfiniteListStates
			isEmpty={!calls.length}
			error={query.error}
			isLoading={query.isLoading}
			isFetching={query.isFetching}
			isRefetchError={query.isRefetchError}
			isFetchingNextPage={query.isFetchingNextPage}
			isFetchNextPageError={query.isFetchNextPageError}
			hasNextPage={query.hasNextPage}
			refetch={query.refetch}
			fetchNextPage={query.fetchNextPage}
			loadingLabel='Loading incoming calls'
			emptyText='No incoming calls yet'
			header={
				<div className={css(styleSheet.columns, styleSheet.incomingGrid)} aria-hidden='true'>
					{INCOMING_COLUMN_LABELS.map(label => (
						<span key={label}>{label}</span>
					))}
				</div>
			}
			errorText="We couldn't load your incoming calls."
		>
			<div className={css(styleSheet.list)}>
				{calls.map((call, index) => (
					<IncomingCallRow
						key={call.phoneCall?.id ?? `incoming-${index}`}
						call={call}
						isSelected={!!selectedCallId && call.phoneCall?.id === selectedCallId}
						onOpenCall={onOpenCall}
					/>
				))}
			</div>
		</InfiniteListStates>
	);
};
